import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";

import ConfirmDialog from "../../../components/ConfirmDialog";

export type ApiKeyRecord = {
  id: number | string;
  name: string;
  prefix: string;
  organization: number | string | null;
  last_used_at: string | null;
  expires_at: string | null;
  createdAt: string;
  updatedAt: string;
};

type CreatedApiKey = ApiKeyRecord & { key: string };

const CreateApiKeyModal: React.FC<{
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  organizations: Array<{ id: number | string; name: string }>;
  onCreated: (created: ApiKeyRecord) => void;
}> = ({ openModal, setOpenModal, organizations, onCreated }) => {
  const intl = useIntl();
  const [name, setName] = useState("");
  const [organizationId, setOrganizationId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const resetForm = () => {
    setName("");
    setOrganizationId("");
    setLoading(false);
    setCreatedKey(null);
    setCopied(false);
  };

  const closeModal = () => {
    // Clear plaintext key from memory when the modal closes.
    resetForm();
    setOpenModal(false);
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error(
        intl.formatMessage({
          id: "general.Name-is-required",
          defaultMessage: "Name is required",
        })
      );
      return;
    }

    setLoading(true);
    try {
      const payload: { name: string; organization?: number } = {
        name: trimmedName,
      };
      if (organizationId) {
        payload.organization = Number(organizationId);
      }

      const res = await axios.post<CreatedApiKey>("/api-keys", payload);
      const { key, ...publicKey } = res.data;
      setCreatedKey(key);
      onCreated(publicKey);
      toast.success(
        intl.formatMessage({
          id: "api-keys.created-success",
          defaultMessage: "API key created",
        })
      );
    } catch (e: any) {
      console.error(e);
      toast.error(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          intl.formatMessage({
            id: "api-keys.create-error",
            defaultMessage: "Could not create API key",
          })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      toast.success(
        intl.formatMessage({
          id: "api-keys.copied",
          defaultMessage: "API key copied to clipboard",
        })
      );
    } catch (e) {
      console.error(e);
      toast.error(
        intl.formatMessage({
          id: "api-keys.copy-error",
          defaultMessage: "Could not copy API key",
        })
      );
    }
  };

  return (
    <Dialog open={openModal} onClose={closeModal} maxWidth="sm" fullWidth>
      <DialogTitle>
        {createdKey ? (
          <FormattedMessage
            id="api-keys.created-title"
            defaultMessage="API key created"
          />
        ) : (
          <FormattedMessage
            id="api-keys.create-title"
            defaultMessage="Create API key"
          />
        )}
      </DialogTitle>
      <DialogContent>
        {createdKey ? (
          <Box sx={{ pt: 1 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <FormattedMessage
                id="api-keys.show-once-warning"
                defaultMessage="Copy this key now. You will not be able to see it again."
              />
            </Alert>
            <TextField
              fullWidth
              value={createdKey}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={handleCopy}
                    edge="end"
                    aria-label={intl.formatMessage({
                      id: "api-keys.copy",
                      defaultMessage: "Copy",
                    })}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
            {copied && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                <FormattedMessage
                  id="api-keys.copied"
                  defaultMessage="API key copied to clipboard"
                />
              </Typography>
            )}
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                fullWidth
                required
                label={intl.formatMessage({
                  id: "general.Name",
                  defaultMessage: "Name",
                })}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="api-key-org-label">
                  <FormattedMessage
                    id="api-keys.organization-optional"
                    defaultMessage="Organization (optional)"
                  />
                </InputLabel>
                <Select
                  labelId="api-key-org-label"
                  value={organizationId}
                  label={intl.formatMessage({
                    id: "api-keys.organization-optional",
                    defaultMessage: "Organization (optional)",
                  })}
                  onChange={(e) => setOrganizationId(String(e.target.value))}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>
                      <FormattedMessage
                        id="api-keys.no-organization"
                        defaultMessage="No organization"
                      />
                    </em>
                  </MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={String(org.id)}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {createdKey ? (
          <Button onClick={closeModal} variant="contained">
            <FormattedMessage id="general.Done" defaultMessage="Done" />
          </Button>
        ) : loading ? (
          <CircularProgress size={28} />
        ) : (
          <>
            <Button onClick={closeModal}>
              <FormattedMessage id="general.Cancel" defaultMessage="Cancel" />
            </Button>
            <Button onClick={handleCreate} variant="contained">
              <FormattedMessage id="general.Create" defaultMessage="Create" />
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const ApiKeysSection = () => {
  const intl = useIntl();
  const organizations = useSelector((state: any) => state.organizations || []);

  const [apiKeys, setApiKeys] = useState<ApiKeyRecord[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<ApiKeyRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const orgNameById = useCallback(
    (orgId: number | string | null) => {
      if (orgId == null || orgId === "") return null;
      const org = organizations.find(
        (o: any) => String(o.id) === String(orgId)
      );
      return org?.name || String(orgId);
    },
    [organizations]
  );

  const loadApiKeys = useCallback(async () => {
    try {
      const res = await axios.get<ApiKeyRecord[]>("/api-keys");
      setApiKeys(Array.isArray(res.data) ? res.data : []);
    } catch (e: any) {
      console.error(e);
      toast.error(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          intl.formatMessage({
            id: "api-keys.load-error",
            defaultMessage: "Could not load API keys",
          })
      );
    } finally {
      setPageLoading(false);
    }
  }, [intl]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleDelete = async () => {
    if (!keyToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`/api-keys/${keyToDelete.id}`);
      setApiKeys((prev) => prev.filter((k) => k.id !== keyToDelete.id));
      toast.success(
        intl.formatMessage({
          id: "api-keys.deleted-success",
          defaultMessage: "API key deleted",
        })
      );
    } catch (e: any) {
      console.error(e);
      toast.error(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          intl.formatMessage({
            id: "api-keys.delete-error",
            defaultMessage: "Could not delete API key",
          })
      );
    } finally {
      setKeyToDelete(null);
      setOpenConfirmModal(false);
      setDeleting(false);
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) {
      return intl.formatMessage({
        id: "api-keys.never",
        defaultMessage: "Never",
      });
    }
    return moment(value).format("LL");
  };

  if (pageLoading) {
    return (
      <Box
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: 140,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6">
            <FormattedMessage id="api-keys.title" defaultMessage="API keys" />
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage
              id="api-keys.description"
              defaultMessage="Create personal API keys to access Gotologic from external systems. Keys are shown only once when created."
            />
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => setOpenCreateModal(true)}
          sx={{ flexShrink: 0 }}
        >
          <FormattedMessage
            id="api-keys.create"
            defaultMessage="Create API key"
          />
        </Button>
      </Box>

      {apiKeys.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          <FormattedMessage
            id="api-keys.empty"
            defaultMessage="You don't have any API keys yet."
          />
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <FormattedMessage id="general.Name" defaultMessage="Name" />
                </TableCell>
                <TableCell>
                  <FormattedMessage
                    id="api-keys.prefix"
                    defaultMessage="Prefix"
                  />
                </TableCell>
                <TableCell>
                  <FormattedMessage
                    id="general.Organization"
                    defaultMessage="Organization"
                  />
                </TableCell>
                <TableCell>
                  <FormattedMessage
                    id="api-keys.last-used"
                    defaultMessage="Last used"
                  />
                </TableCell>
                <TableCell>
                  <FormattedMessage
                    id="general.Created"
                    defaultMessage="Created"
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((apiKey) => (
                <TableRow key={apiKey.id}>
                  <TableCell>{apiKey.name}</TableCell>
                  <TableCell>
                    <code>{apiKey.prefix}…</code>
                  </TableCell>
                  <TableCell>
                    {orgNameById(apiKey.organization) || (
                      <Typography variant="body2" color="textSecondary">
                        <FormattedMessage
                          id="api-keys.no-organization"
                          defaultMessage="No organization"
                        />
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(apiKey.last_used_at)}</TableCell>
                  <TableCell>{formatDate(apiKey.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setKeyToDelete(apiKey);
                        setOpenConfirmModal(true);
                      }}
                    >
                      <FormattedMessage
                        id="general.Delete"
                        defaultMessage="Delete"
                      />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateApiKeyModal
        openModal={openCreateModal}
        setOpenModal={setOpenCreateModal}
        organizations={organizations}
        onCreated={(created) => {
          setApiKeys((prev) => [created, ...prev]);
        }}
      />

      <ConfirmDialog
        loading={deleting}
        openModal={openConfirmModal}
        setOpenModal={(open) => {
          setOpenConfirmModal(open);
          if (!open) setKeyToDelete(null);
        }}
        confirmHandler={handleDelete}
        confirmLabelId="general.Delete"
        text={intl.formatMessage(
          {
            id: "api-keys.delete-confirm",
            defaultMessage:
              'Are you sure you want to delete the API key "{name}"? Applications using this key will stop working.',
          },
          { name: keyToDelete?.name || "" }
        )}
      />
    </Box>
  );
};

export default ApiKeysSection;
